import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import type { PaperProps } from '@mui/material';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Category } from '../../types/Category';
import {
  categorySubtreeHasAllowedCategoryId,
  countContentRowsInCategorySubtree,
  findCategoryInTree,
  findDocumentRootCategory,
  type AddMatchingContentSourceGroup,
} from '../../utils/CategoryUtils';
import { CategoryPickerTreeLabel } from './CompareContentDialog';
import { Content } from '../../types/Content';
import api from '../../services/api';
import {
  AddMatchingContentMatchPreview,
  applyAddMatchingContent,
  mapAddMatchingContentTransfers,
  previewAddMatchingContent,
} from '../../services/CategoryService';
import { useSearchSettings } from '../../contexts/SearchSettingsContext';
import { useHelpContent } from '../../utils/HelpUtils';
import ContentRichPreviewDialog from '../content/ContentRichPreviewDialog';
import AdjustTextDialog from './AdjustTextDialog';
import { prepareContentRichPreviewHtml } from '../../utils/contentRichPreviewHtml';

export interface AddMatchingContentTransfer {
  contentId: string;
  sourceContentId?: string;
  sourceCategoryId: string;
  targetCategoryId: string;
  moved: boolean;
}

/**
 * Dialog Paper that can be dragged by an element marked with data-drag-handle="true" (the title),
 * so the user can move the dialog aside to view the category tree. Defined at module scope so it is
 * a stable component type (a PaperComponent redefined per render would remount the dialog content).
 */
const DraggablePaper: React.FC<PaperProps> = (props) => {
  // Start shifted toward the right so the dialog doesn't cover the (left-side) category tree.
  // Approximate the maxWidth="sm" paper (~600px) and clamp so it stays fully on screen.
  const [pos, setPos] = useState(() => {
    if (typeof window === 'undefined') return { x: 0, y: 0 };
    const maxRightOffset = Math.max(0, (window.innerWidth - 600) / 2 - 16);
    return { x: Math.round(maxRightOffset * 0.5), y: 0 };
  });
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      setPos({
        x: drag.origX + (e.clientX - drag.startX),
        y: drag.origY + (e.clientY - drag.startY),
      });
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest('[data-drag-handle="true"]')) return;
    e.preventDefault();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: pos.x,
      origY: pos.y,
    };
  };

  return (
    <Paper
      {...props}
      onMouseDown={handleMouseDown}
      style={{ ...props.style, transform: `translate(${pos.x}px, ${pos.y}px)` }}
    />
  );
};

function collectAncestorCategoryIds(categoriesTree: Category[], categoryId: string): string[] {
  const ids: string[] = [];
  let parentId = findCategoryInTree(categoriesTree, categoryId)?.parentId;
  while (parentId) {
    ids.push(parentId);
    parentId = findCategoryInTree(categoriesTree, parentId)?.parentId;
  }
  return ids;
}

interface AddMatchingContentDialogProps {
  open: boolean;
  onClose: () => void;
  targetCategory: Category | null;
  categoriesTree: Category[];
  sourceGroups: AddMatchingContentSourceGroup[];
  defaultSourceId?: string | null;
  suggestedSourceExamples?: string[];
  onSuccess: (targetCategoryId: string, transfers: AddMatchingContentTransfer[], move: boolean) => void;
  /**
   * Notifies the parent that a source row's text was edited in place, with the previous row state,
   * so it can sync its tree and record an undoable history entry.
   */
  onSourceRowEdited?: (categoryId: string, updated: Content, previous?: Content) => void;
}

const CHOOSE_SOURCE_MESSAGE = 'Choose a category to copy or move content from.';

type DialogStep = 'setup' | 'review';
type ApplyMode = 'all' | 'step' | 'quick';

const matchPreviewText = (
  match: AddMatchingContentMatchPreview,
  content: Content | undefined,
  maxLen = 200,
): string => {
  const raw = content?.text?.trim() || match.textPreview?.trim() || '(empty)';
  if (raw.length <= maxLen) return raw;
  return `${raw.slice(0, maxLen)}…`;
};

const formatMatchSimilarityPercent = (score: number | undefined): string | null => {
  if (score === undefined || Number.isNaN(score)) return null;
  return `${(score * 100).toFixed(1)}%`;
};

function extractApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const data = (err as { response?: { data?: unknown } }).response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object' && 'message' in data) {
      const msg = (data as { message?: unknown }).message;
      if (typeof msg === 'string' && msg.trim()) return msg;
    }
  }
  return fallback;
}

const AddMatchingContentDialog: React.FC<AddMatchingContentDialogProps> = ({
  open,
  onClose,
  targetCategory,
  categoriesTree,
  sourceGroups,
  defaultSourceId,
  suggestedSourceExamples,
  onSuccess,
  onSourceRowEdited,
}) => {
  const { settings, updateSettings } = useSearchSettings();
  const { addMatchingShowSimilarityScore } = settings;
  const { getTooltipTextForState } = useHelpContent();
  const [step, setStep] = useState<DialogStep>('setup');
  const [sourceCategoryId, setSourceCategoryId] = useState('');
  const [moveMode, setMoveMode] = useState<'copy' | 'move'>('copy');
  const [applyMode, setApplyMode] = useState<ApplyMode>('step');
  const [quickSelectedIds, setQuickSelectedIds] = useState<Set<string>>(new Set());
  const [matches, setMatches] = useState<AddMatchingContentMatchPreview[]>([]);
  const [reviewIndex, setReviewIndex] = useState(0);
  // Step-by-step decisions are deferred: recorded per source row id and applied in one
  // batch at the end, so the user can move back/forward and change their mind freely.
  const [decisions, setDecisions] = useState<Map<string, 'accepted' | 'skipped'>>(new Map());
  const [sourceContentsById, setSourceContentsById] = useState<Map<string, Content>>(new Map());
  // Source rows whose text was edited in place during this review (for the "Edited" marker), plus a
  // snapshot of each one's original text so the edit can be reverted. Edits are persisted on Save.
  const [editedRowIds, setEditedRowIds] = useState<Set<string>>(new Set());
  const [originalTextById, setOriginalTextById] = useState<Map<string, string>>(new Map());
  const [stitchOpen, setStitchOpen] = useState(false);
  const [stitchRowId, setStitchRowId] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [richPreviewOpen, setRichPreviewOpen] = useState(false);
  const [confirmApplyOpen, setConfirmApplyOpen] = useState(false);
  const previewRequestGenRef = useRef(0);
  // Tracks the open/target "session" so the reset effect runs only on (re)open or target change,
  // not when sourceGroups/sourceOptions get a new identity (e.g. after editing a source row).
  const initSessionKeyRef = useRef<string | null>(null);

  const sourceOptions = useMemo(
    () => sourceGroups.flatMap((g) => g.sources).filter((c) => (c.contents?.length ?? 0) > 0),
    [sourceGroups],
  );

  const allowedSourceIds = useMemo(
    () => new Set(sourceOptions.map(c => c.id)),
    [sourceOptions],
  );

  const documentRoot = useMemo(
    () =>
      targetCategory ? findDocumentRootCategory(categoriesTree, targetCategory.id) : null,
    [categoriesTree, targetCategory],
  );

  const defaultExpanded = useMemo(() => {
    if (!documentRoot || !targetCategory) return [];
    const ids = new Set<string>([documentRoot.id, targetCategory.id]);
    collectAncestorCategoryIds(categoriesTree, targetCategory.id).forEach(id => ids.add(id));
    if (sourceCategoryId) {
      ids.add(sourceCategoryId);
      collectAncestorCategoryIds(categoriesTree, sourceCategoryId).forEach(id => ids.add(id));
    }
    return Array.from(ids);
  }, [documentRoot, targetCategory, sourceCategoryId, categoriesTree]);

  const handleSourceTreeSelect = (_event: React.SyntheticEvent, nodeId: string) => {
    if (allowedSourceIds.has(nodeId)) {
      setSourceCategoryId(nodeId);
    }
  };

  const renderSourceTreeNode = (category: Category): React.ReactNode | null => {
    if (!targetCategory) return null;
    const isDestination = category.id === targetCategory.id;
    const selectable = allowedSourceIds.has(category.id);
    if (
      !isDestination &&
      !selectable &&
      !categorySubtreeHasAllowedCategoryId(category, allowedSourceIds)
    ) {
      return null;
    }
    const hasChildren = (category.children?.length ?? 0) > 0;
    const rowCount = category.contentCategory
      ? (category.contents?.length ?? 0)
      : countContentRowsInCategorySubtree(category);
    let nameSuffix = '';
    if (isDestination) {
      nameSuffix = ' (destination)';
    } else if (selectable && defaultSourceId === category.id) {
      nameSuffix = ' (suggested)';
    }
    const nameColor = isDestination
      ? 'text.disabled'
      : selectable
        ? 'text.primary'
        : 'text.secondary';

    return (
      <TreeItem
        key={category.id}
        nodeId={category.id}
        label={
          <CategoryPickerTreeLabel
            category={category}
            contentCount={rowCount}
            nameColor={nameColor}
            nameSuffix={nameSuffix}
          />
        }
        disabled={isDestination}
      >
        {hasChildren &&
          category.children!.map(child => renderSourceTreeNode(child)).filter(Boolean)}
      </TreeItem>
    );
  };

  const resetReviewState = useCallback(() => {
    setStep('setup');
    setReviewIndex(0);
    setDecisions(new Map());
    setSourceContentsById(new Map());
    setEditedRowIds(new Set());
    setOriginalTextById(new Map());
    setStitchOpen(false);
    setStitchRowId(null);
    setRichPreviewOpen(false);
    setConfirmApplyOpen(false);
    setQuickSelectedIds(new Set());
  }, []);

  const refreshPreview = useCallback(async (targetId: string, sourceId: string) => {
    if (!targetId || !sourceId) {
      setMatches([]);
      setError(null);
      return;
    }
    if (!sourceOptions.some((c) => c.id === sourceId)) {
      setMatches([]);
      setError(null);
      return;
    }
    const requestGen = ++previewRequestGenRef.current;
    setPreviewLoading(true);
    setError(null);
    try {
      const data = await previewAddMatchingContent(targetId, sourceId);
      if (requestGen !== previewRequestGenRef.current) {
        return;
      }
      setMatches(data.matches ?? []);
    } catch (err: unknown) {
      if (requestGen !== previewRequestGenRef.current) {
        return;
      }
      setMatches([]);
      const raw = extractApiErrorMessage(err, 'Failed to preview matching content.');
      if (
        raw.includes('required') ||
        raw.toLowerCase().includes('not found') ||
        raw.toLowerCase().includes('no content')
      ) {
        setError(CHOOSE_SOURCE_MESSAGE);
      } else {
        setError(raw);
      }
    } finally {
      setPreviewLoading(false);
    }
  }, [sourceOptions]);

  useEffect(() => {
    if (!open || !targetCategory) {
      initSessionKeyRef.current = null;
      return;
    }
    // Only (re)initialize once per open/target session. Re-running on every sourceOptions identity
    // change would kick the user out of step-by-step review back to setup after a source-row edit.
    if (initSessionKeyRef.current === targetCategory.id) {
      return;
    }
    initSessionKeyRef.current = targetCategory.id;
    const initial =
      defaultSourceId && sourceOptions.some((c) => c.id === defaultSourceId)
        ? defaultSourceId
        : '';
    setSourceCategoryId(initial);
    setMoveMode('copy');
    setApplyMode('step');
    setError(null);
    resetReviewState();
    if (!initial) {
      setMatches([]);
    }
  }, [open, targetCategory, defaultSourceId, sourceOptions, resetReviewState]);

  useEffect(() => {
    if (!open || !targetCategory || !sourceCategoryId || step !== 'setup') {
      return;
    }
    void refreshPreview(targetCategory.id, sourceCategoryId);
  }, [sourceCategoryId, open, targetCategory, refreshPreview, step]);

  const exitReview = async (closeDialog: boolean) => {
    // Decisions are applied atomically via Apply; cancelling/closing applies nothing.
    resetReviewState();
    if (!closeDialog && targetCategory && sourceCategoryId) {
      await refreshPreview(targetCategory.id, sourceCategoryId);
    }
    if (closeDialog) {
      onClose();
    }
  };

  const handleCancelReview = () => void exitReview(false);

  const handleClose = () => {
    if (step === 'review') {
      void exitReview(true);
      return;
    }
    resetReviewState();
    onClose();
  };

  const loadSourceContents = async (sourceId: string) => {
    const response = await api.get<Content[]>(
      `/content/list?categoryId=${encodeURIComponent(sourceId)}`,
    );
    const map = new Map<string, Content>();
    for (const row of response.data ?? []) {
      if (row?.id) {
        map.set(row.id, row);
      }
    }
    setSourceContentsById(map);
  };

  const handleStartReview = async () => {
    if (!targetCategory || !sourceCategoryId || matches.length === 0) return;
    setReviewLoading(true);
    setError(null);
    try {
      await loadSourceContents(sourceCategoryId);
      setReviewIndex(0);
      setDecisions(new Map());
      if (applyMode === 'quick') {
        setQuickSelectedIds(
          new Set(matches.map((m) => m.contentId).filter((id): id is string => !!id)),
        );
      } else {
        setQuickSelectedIds(new Set());
      }
      setStep('review');
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err, 'Failed to load source rows for review.'));
    } finally {
      setReviewLoading(false);
    }
  };

  const handleApplyAll = async () => {
    if (!targetCategory || !sourceCategoryId) return;
    const ids = matches.map((m) => m.contentId).filter((id): id is string => !!id);
    if (ids.length === 0) return;
    setApplyLoading(true);
    setError(null);
    try {
      const data = await applyAddMatchingContent(
        targetCategory.id,
        sourceCategoryId,
        moveMode === 'move',
        ids,
      );
      const transfers = mapAddMatchingContentTransfers(data.transfers ?? [], moveMode === 'move');
      onSuccess(targetCategory.id, transfers, moveMode === 'move');
      handleClose();
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err, 'Failed to add matching content.'));
    } finally {
      setApplyLoading(false);
    }
  };

  const finishReview = (transfers: AddMatchingContentTransfer[]) => {
    if (!targetCategory) return;
    onSuccess(targetCategory.id, transfers, moveMode === 'move');
    handleClose();
  };

  const goToPrevMatch = () => {
    setReviewIndex((i) => Math.max(0, i - 1));
  };

  const goToNextMatch = () => {
    setReviewIndex((i) => Math.min(matches.length - 1, i + 1));
  };

  const recordDecision = (decision: 'accepted' | 'skipped') => {
    const current = matches[reviewIndex];
    if (!current?.contentId) return;
    const id = current.contentId;
    const nextDecisions = new Map(decisions);
    nextDecisions.set(id, decision);
    setDecisions(nextDecisions);
    // Advance one row. We deliberately do NOT jump to the frontier so re-deciding an
    // earlier row keeps the user in place; the "resume point" marker shows where they
    // left off so they can navigate back when ready.
    setReviewIndex((i) => Math.min(i + 1, matches.length - 1));
  };

  const handleRejectCurrent = () => recordDecision('skipped');

  const handleApplyQuickSelected = async () => {
    if (!targetCategory || !sourceCategoryId) return;
    const ids = matches
      .map((m) => m.contentId)
      .filter((id): id is string => !!id && quickSelectedIds.has(id));
    if (ids.length === 0) {
      setError('Select at least one row to apply.');
      return;
    }
    setApplyLoading(true);
    setError(null);
    try {
      const data = await applyAddMatchingContent(
        targetCategory.id,
        sourceCategoryId,
        moveMode === 'move',
        ids,
      );
      const transfers = mapAddMatchingContentTransfers(data.transfers ?? [], moveMode === 'move');
      finishReview(transfers);
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err, 'Failed to apply selected rows.'));
    } finally {
      setApplyLoading(false);
    }
  };

  const quickMatchIds = useMemo(
    () => matches.map((m) => m.contentId).filter((id): id is string => !!id),
    [matches],
  );

  const allQuickSelected =
    quickMatchIds.length > 0 && quickSelectedIds.size === quickMatchIds.length;
  const someQuickSelected =
    quickSelectedIds.size > 0 && quickSelectedIds.size < quickMatchIds.length;

  const handleQuickSelectAll = (checked: boolean) => {
    setQuickSelectedIds(checked ? new Set(quickMatchIds) : new Set());
  };

  const toggleQuickMatch = (contentId: string) => {
    setQuickSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(contentId)) {
        next.delete(contentId);
      } else {
        next.add(contentId);
      }
      return next;
    });
  };

  const handleAcceptCurrent = () => recordDecision('accepted');

  // Keyboard shortcuts for step-by-step review: ←/→ navigate, A accepts, S skips. Disabled while a
  // sub-dialog is open, while applying, or when a text field/contenteditable has focus.
  useEffect(() => {
    if (step !== 'review' || applyMode !== 'step') return undefined;
    if (stitchOpen || confirmApplyOpen) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (applyLoading || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable) return;
      const current = matches[reviewIndex];
      const decide = (decision: 'accepted' | 'skipped') => {
        if (!current?.contentId) return;
        e.preventDefault();
        const id = current.contentId;
        setDecisions((prev) => new Map(prev).set(id, decision));
        setReviewIndex((i) => Math.min(i + 1, matches.length - 1));
      };
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          setReviewIndex((i) => Math.max(0, i - 1));
          break;
        case 'ArrowRight':
          e.preventDefault();
          setReviewIndex((i) => Math.min(matches.length - 1, i + 1));
          break;
        case 'a':
        case 'A':
          decide('accepted');
          break;
        case 's':
        case 'S':
          decide('skipped');
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [step, applyMode, stitchOpen, confirmApplyOpen, applyLoading, reviewIndex, matches]);

  const handleApplyAccepted = async () => {
    if (!targetCategory || !sourceCategoryId) return;
    const ids = matches
      .map((m) => m.contentId)
      .filter((id): id is string => !!id && decisions.get(id) === 'accepted');
    if (ids.length === 0) {
      setError('Accept at least one row to apply.');
      return;
    }
    setApplyLoading(true);
    setError(null);
    try {
      const data = await applyAddMatchingContent(
        targetCategory.id,
        sourceCategoryId,
        moveMode === 'move',
        ids,
      );
      const transfers = mapAddMatchingContentTransfers(data.transfers ?? [], moveMode === 'move');
      finishReview(transfers);
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err, 'Failed to apply accepted rows.'));
    } finally {
      setApplyLoading(false);
    }
  };

  const currentMatch = matches[reviewIndex];
  const currentDecision = currentMatch?.contentId ? decisions.get(currentMatch.contentId) : undefined;
  const acceptedCount = useMemo(
    () => matches.filter((m) => m.contentId && decisions.get(m.contentId) === 'accepted').length,
    [matches, decisions],
  );
  const skippedCount = useMemo(
    () => matches.filter((m) => m.contentId && decisions.get(m.contentId) === 'skipped').length,
    [matches, decisions],
  );
  const decidedCount = acceptedCount + skippedCount;
  const totalDecidable = useMemo(() => matches.filter((m) => !!m.contentId).length, [matches]);
  const undecidedCount = Math.max(0, totalDecidable - decidedCount);
  // "Resume point" = the row right after the furthest decided row, i.e. where the user left
  // off scanning. Shown as a marker so they can recognize/return to it while paging around.
  const lastDecidedIndex = useMemo(() => {
    let last = -1;
    matches.forEach((m, idx) => {
      if (m.contentId && decisions.has(m.contentId)) last = idx;
    });
    return last;
  }, [matches, decisions]);
  const frontierIndex =
    matches.length > 0 ? Math.min(lastDecidedIndex + 1, matches.length - 1) : 0;
  const hasResumePoint = decidedCount > 0 && lastDecidedIndex < matches.length - 1;
  const atResumePoint = hasResumePoint && reviewIndex === frontierIndex;
  const currentContent = currentMatch ? sourceContentsById.get(currentMatch.contentId) : undefined;
  const isCurrentEdited = !!currentMatch?.contentId && editedRowIds.has(currentMatch.contentId);
  // Edited rows are stored as plain text, so their rich preview is naturally cleared.
  const displayHtml = currentContent?.richTextHtml?.trim()
    ? prepareContentRichPreviewHtml(currentContent.richTextHtml)
    : null;
  const displayText = currentMatch
    ? matchPreviewText(currentMatch, currentContent, 4000)
    : '(empty)';
  const displayIsRTL = /[\u0590-\u05FF\u0600-\u06FF]/.test(displayText);

  // Neighbours follow the review navigation order (the rows reached with Back / Next), so the
  // stitch prev/next stay consistent with what the user is stepping through.
  // Read the freshest subcase for a match (edited rows in sourceContentsById win over the preview).
  const subcaseForMatch = (match?: { contentId?: string; subcase?: string }): string =>
    (match?.contentId ? sourceContentsById.get(match.contentId)?.subcase : undefined) ??
    match?.subcase ??
    '';
  const currentSubcase = subcaseForMatch(currentMatch);

  const prevMatch = reviewIndex > 0 ? matches[reviewIndex - 1] : undefined;
  const nextMatch = reviewIndex < matches.length - 1 ? matches[reviewIndex + 1] : undefined;
  // A side is only offered when that neighbour shares the current row's subcase — borrowing across
  // subcases doesn't make sense (mirrors the content-pane Adjust text rule).
  const hasPrevNeighbour = !!prevMatch?.contentId && subcaseForMatch(prevMatch) === currentSubcase;
  const hasNextNeighbour = !!nextMatch?.contentId && subcaseForMatch(nextMatch) === currentSubcase;
  const prevNeighbourText = hasPrevNeighbour
    ? (sourceContentsById.get(prevMatch!.contentId)?.text ?? prevMatch!.textPreview ?? '')
    : '';
  const nextNeighbourText = hasNextNeighbour
    ? (sourceContentsById.get(nextMatch!.contentId)?.text ?? nextMatch!.textPreview ?? '')
    : '';
  const canStitchCurrent =
    applyMode === 'step' &&
    !!currentMatch?.contentId &&
    (hasPrevNeighbour || hasNextNeighbour);

  const currentBaseText = (currentContent?.text ?? currentMatch?.textPreview ?? '').toString();

  const openStitchDialog = () => {
    if (!currentMatch?.contentId) return;
    setStitchRowId(currentMatch.contentId);
    setStitchOpen(true);
  };

  // Persist a source row's text in place (POST /content with an id performs an update). Rich
  // text/delta are cleared because the stitched text is plain; attachments are preserved.
  const persistSourceRowText = async (rowId: string, newText: string): Promise<Content> => {
    const existing = sourceContentsById.get(rowId);
    const payload: Record<string, unknown> = {
      parentId: sourceCategoryId,
      id: rowId,
      subcase: existing?.subcase ?? currentMatch?.subcase ?? '',
      text: newText,
      richTextHtml: '',
      richTextDelta: '',
      ...(typeof existing?.attachmentsJson === 'string'
        ? { attachmentsJson: existing.attachmentsJson }
        : {}),
    };
    const response = await api.post<Content>('/content', payload);
    const updated = response.data;
    setSourceContentsById((prev) => {
      const next = new Map(prev);
      next.set(rowId, updated);
      return next;
    });
    onSourceRowEdited?.(sourceCategoryId, updated, existing);
    return updated;
  };

  const handleStitchSave = async (newText: string) => {
    const rowId = stitchRowId;
    if (!rowId) {
      setStitchOpen(false);
      return;
    }
    const beforeText = (sourceContentsById.get(rowId)?.text ?? currentMatch?.textPreview ?? '')
      .toString();
    if (!newText.trim() || newText === beforeText) {
      setStitchOpen(false);
      return;
    }
    // persistSourceRowText throws on failure — the AdjustTextDialog surfaces the error and stays open.
    await persistSourceRowText(rowId, newText);
    setOriginalTextById((prev) => (prev.has(rowId) ? prev : new Map(prev).set(rowId, beforeText)));
    setEditedRowIds((prev) => new Set(prev).add(rowId));
    setStitchOpen(false);
  };

  const revertCurrentEdit = async () => {
    const rowId = currentMatch?.contentId;
    if (!rowId) return;
    const original = originalTextById.get(rowId);
    if (original === undefined) return;
    setApplyLoading(true);
    setError(null);
    try {
      await persistSourceRowText(rowId, original);
      setEditedRowIds((prev) => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
      setOriginalTextById((prev) => {
        const next = new Map(prev);
        next.delete(rowId);
        return next;
      });
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err, 'Failed to revert the edited text.'));
    } finally {
      setApplyLoading(false);
    }
  };

  const sourceExamplesHint =
    suggestedSourceExamples && suggestedSourceExamples.length > 0
      ? suggestedSourceExamples.join(' or ')
      : 'Temp — … or Original Files Content';

  const matchCount = matches.length;
  const reviewProgress =
    applyMode === 'step' && matches.length > 0
      ? (decidedCount / matches.length) * 100
      : 0;

  return (
    <>
      <Dialog
        open={open}
        onClose={(_event, reason) => {
          // During step-by-step review, don't let a stray backdrop click or Escape abandon the
          // whole operation — exit only via "Cancel review" or "Apply".
          if (step === 'review' && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
            return;
          }
          handleClose();
        }}
        maxWidth="sm"
        fullWidth
        PaperComponent={DraggablePaper}
        slotProps={{ backdrop: { sx: { backgroundColor: 'transparent' } } }}
      >
        <DialogTitle data-drag-handle="true" sx={{ cursor: 'move' }}>
          {step === 'setup'
            ? 'Add matching rows from another category (AI)'
            : applyMode === 'quick'
              ? 'Quick review — select rows'
              : 'Review matching rows'}
        </DialogTitle>
        <DialogContent>
          {step === 'setup' && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Copy or move semantically matching rows from another content category into{' '}
              <strong>{targetCategory?.name ?? 'this category'}</strong> (same threshold as Find in
              Document). Pick a source content category in the tree (folders are for navigation only).
              Rows already in the target (same subcase and text) are skipped.
            </Typography>

            {sourceOptions.length === 0 ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No content categories with rows were found along the path from this category up to the
                document root. Typical sources after a split are{' '}
                <strong>{sourceExamplesHint}</strong>.
              </Alert>
            ) : (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                  Copy or move from
                </Typography>
                {documentRoot && (
                  <Box
                    sx={{
                      maxHeight: 280,
                      overflow: 'auto',
                      border: theme => `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      p: 0.5,
                    }}
                  >
                    <TreeView
                      key={`${documentRoot.id}-${targetCategory?.id}`}
                      defaultCollapseIcon={<ExpandMoreIcon />}
                      defaultExpandIcon={<ChevronRightIcon />}
                      defaultExpanded={defaultExpanded}
                      selected={sourceCategoryId || ''}
                      onNodeSelect={handleSourceTreeSelect}
                    >
                      {renderSourceTreeNode(documentRoot)}
                    </TreeView>
                  </Box>
                )}
              </Box>
            )}

              {!sourceCategoryId && sourceOptions.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {CHOOSE_SOURCE_MESSAGE}
                </Alert>
              )}

              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Action
                </Typography>
                <RadioGroup
                  row
                  value={moveMode}
                  onChange={(e) => setMoveMode(e.target.value as 'copy' | 'move')}
                >
                  <FormControlLabel value="copy" control={<Radio />} label="Copy rows" />
                  <FormControlLabel value="move" control={<Radio />} label="Move rows" />
                </RadioGroup>
              </FormControl>

              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  How to apply
                </Typography>
                <RadioGroup
                  value={applyMode}
                  onChange={(e) => setApplyMode(e.target.value as ApplyMode)}
                >
                  <FormControlLabel value="all" control={<Radio />} label="Apply all matches at once" />
                  <FormControlLabel
                    value="step"
                    control={<Radio />}
                    label="Step-by-step review (accept or skip each row)"
                  />
                  <FormControlLabel
                    value="quick"
                    control={<Radio />}
                    label="Quick review (checkbox list, apply selected)"
                  />
                </RadioGroup>
              </FormControl>

              <Tooltip title={getTooltipTextForState('add-matching-show-similarity-score', true)}>
                <span>
                  <FormControlLabel
                    sx={{ mb: 2, display: 'block' }}
                    control={
                      <Checkbox
                        checked={addMatchingShowSimilarityScore}
                        onChange={async (e) => {
                          const checked = e.target.checked;
                          updateSettings({ addMatchingShowSimilarityScore: checked });
                          try {
                            await api.put('/search/settings', {
                              search: { addMatchingShowSimilarityScore: checked },
                            });
                          } catch (err) {
                            console.error('Error updating add matching settings:', err);
                          }
                        }}
                        size="small"
                      />
                    }
                    label="Show similarity scores on matched rows"
                  />
                </span>
              </Tooltip>

              {sourceCategoryId && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 28 }}>
                  {previewLoading && <CircularProgress size={20} />}
                  <Typography variant="body2">
                    {previewLoading
                      ? 'Checking matches…'
                      : matchCount === 0
                        ? 'No matching rows found in the selected source.'
                        : `${matchCount} matching row${matchCount === 1 ? '' : 's'} found.`}
                  </Typography>
                </Box>
              )}
            </>
          )}

          {step === 'review' && applyMode === 'step' && currentMatch && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Destination: <strong>{targetCategory?.name ?? 'this category'}</strong>
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'nowrap',
                  mb: 1,
                  alignItems: 'center',
                  overflow: 'hidden',
                }}
              >
                <Chip size="small" label={`${reviewIndex + 1} of ${matches.length}`} />
                {/* The resume ("You stopped here") chip already conveys the undecided state,
                    so skip the redundant "not decided" chip on that row. */}
                {!(atResumePoint && !currentDecision) && (
                  <Chip
                    size="small"
                    variant={currentDecision ? 'filled' : 'outlined'}
                    color={
                      currentDecision === 'accepted'
                        ? 'success'
                        : currentDecision === 'skipped'
                          ? 'default'
                          : 'warning'
                    }
                    label={
                      currentDecision === 'accepted'
                        ? `This row: ${moveMode === 'move' ? 'Move' : 'Copy'}`
                        : currentDecision === 'skipped'
                          ? 'This row: Skip'
                          : 'This row: not decided'
                    }
                  />
                )}
                {hasResumePoint && (
                  <Chip
                    size="small"
                    color="info"
                    variant={atResumePoint ? 'filled' : 'outlined'}
                    clickable={!atResumePoint}
                    onClick={atResumePoint ? undefined : () => setReviewIndex(frontierIndex)}
                    label={
                      atResumePoint
                        ? 'You stopped here'
                        : `Resume at #${frontierIndex + 1}`
                    }
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={reviewProgress}
                  sx={{ flexGrow: 1 }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  <Box component="span" sx={{ color: 'success.main' }}>{acceptedCount} accepted</Box>
                  {' · '}
                  {skippedCount} skipped
                  {undecidedCount > 0 && (
                    <>
                      {' · '}
                      <Box component="span" sx={{ color: 'warning.main' }}>{undecidedCount} undecided</Box>
                    </>
                  )}
                </Typography>
              </Box>
              {currentMatch.subcase && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 1, textAlign: displayIsRTL ? 'right' : 'left' }}
                  dir={displayIsRTL ? 'rtl' : 'ltr'}
                >
                  Subcase: {currentMatch.subcase}
                </Typography>
              )}
              {addMatchingShowSimilarityScore && formatMatchSimilarityPercent(currentMatch.score) && (
                <Typography variant="caption" color="primary" display="block" sx={{ mb: 1 }}>
                  Similarity: {formatMatchSimilarityPercent(currentMatch.score)}
                </Typography>
              )}
              <Typography
                variant="body2"
                dir={displayIsRTL ? 'rtl' : 'ltr'}
                sx={{
                  whiteSpace: 'pre-wrap',
                  maxHeight: 240,
                  overflow: 'auto',
                  mb: 2,
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  border: 2,
                  borderColor:
                    currentDecision === 'accepted'
                      ? 'success.main'
                      : currentDecision === 'skipped'
                        ? 'divider'
                        : atResumePoint
                          ? 'info.main'
                          : 'warning.main',
                  textAlign: displayIsRTL ? 'right' : 'left',
                }}
              >
                {displayText}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                {displayHtml && (
                  <Button size="small" onClick={() => setRichPreviewOpen(true)}>
                    Rich preview
                  </Button>
                )}
                {canStitchCurrent && (
                  <Tooltip title="Borrow text from the end of the previous row or the start of the next row into this row. The edit is saved to this row; the previous and next rows are not changed.">
                    <Button size="small" onClick={openStitchDialog}>
                      {isCurrentEdited ? 'Edit text…' : 'Adjust text…'}
                    </Button>
                  </Tooltip>
                )}
                {isCurrentEdited && (
                  <>
                    <Chip size="small" color="info" variant="outlined" label="Edited" />
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => void revertCurrentEdit()}
                      disabled={applyLoading}
                    >
                      Revert
                    </Button>
                  </>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Use Back / Next to read nearby rows for context. Accept or Skip sets this row’s
                decision — nothing is applied until you choose Apply. The blue “Resume” marker shows
                where you left off so you can jump back after browsing. Shortcuts: ← Back, → Next,
                A Accept, S Skip.
                {canStitchCurrent &&
                  ' Use “Adjust text…” to borrow text from the end of the previous row or the start of the next row into this row (saved to this row when you click Save).'}
              </Typography>
            </Box>
          )}

          {step === 'review' && applyMode === 'quick' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Destination: <strong>{targetCategory?.name ?? 'this category'}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Select rows to {moveMode === 'move' ? 'move' : 'copy'} ({quickSelectedIds.size} of{' '}
                {matches.length} selected).
              </Typography>
              <List dense sx={{ maxHeight: 360, overflow: 'auto' }}>
                <ListItem disablePadding sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <ListItemButton
                    onClick={() => handleQuickSelectAll(!allQuickSelected)}
                    dense
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        size="small"
                        checked={allQuickSelected}
                        indeterminate={someQuickSelected}
                        disabled={quickMatchIds.length === 0}
                        tabIndex={-1}
                        disableRipple
                        onChange={(e) => handleQuickSelectAll(e.target.checked)}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Select all" />
                  </ListItemButton>
                </ListItem>
                {matches.map((m) => {
                  const id = m.contentId;
                  if (!id) return null;
                  const content = sourceContentsById.get(id);
                  const similarityLabel =
                    addMatchingShowSimilarityScore && formatMatchSimilarityPercent(m.score)
                      ? `Similarity: ${formatMatchSimilarityPercent(m.score)}`
                      : undefined;
                  return (
                    <ListItem key={id} disablePadding>
                      <ListItemButton onClick={() => toggleQuickMatch(id)} dense>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            edge="start"
                            checked={quickSelectedIds.has(id)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={m.subcase || '(no subcase)'}
                          secondary={
                            similarityLabel
                              ? `${similarityLabel} · ${matchPreviewText(m, content, 120)}`
                              : matchPreviewText(m, content, 120)
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {step === 'setup' ? (
            <>
              <Button onClick={handleClose}>Cancel</Button>
              {applyMode === 'all' ? (
                <Button
                  variant="contained"
                  onClick={() => void handleApplyAll()}
                  disabled={
                    applyLoading ||
                    previewLoading ||
                    !sourceCategoryId ||
                    matchCount === 0 ||
                    sourceOptions.length === 0
                  }
                >
                  {applyLoading ? 'Applying…' : `Apply ${matchCount} row${matchCount === 1 ? '' : 's'}`}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => void handleStartReview()}
                  disabled={
                    reviewLoading ||
                    previewLoading ||
                    !sourceCategoryId ||
                    matchCount === 0 ||
                    sourceOptions.length === 0
                  }
                >
                  {reviewLoading ? 'Loading…' : 'Start review'}
                </Button>
              )}
            </>
          ) : (
            <>
              <Button onClick={handleCancelReview}>Cancel review</Button>
              {applyMode === 'quick' ? (
                <Button
                  variant="contained"
                  onClick={() => void handleApplyQuickSelected()}
                  disabled={applyLoading || quickSelectedIds.size === 0}
                >
                  {applyLoading
                    ? 'Applying…'
                    : `Apply ${quickSelectedIds.size} selected`}
                </Button>
              ) : (
                <>
                  <Tooltip title="Back (←)">
                    <span>
                      <Button onClick={goToPrevMatch} disabled={applyLoading || reviewIndex === 0}>
                        Back
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Next (→)">
                    <span>
                      <Button
                        onClick={goToNextMatch}
                        disabled={applyLoading || reviewIndex >= matches.length - 1}
                      >
                        Next
                      </Button>
                    </span>
                  </Tooltip>
                  <Box sx={{ flexGrow: 1 }} />
                  <Tooltip title="Skip (S)">
                    <span>
                      <Button
                        onClick={handleRejectCurrent}
                        color="inherit"
                        variant={currentDecision === 'skipped' ? 'contained' : 'outlined'}
                        disabled={applyLoading}
                      >
                        Skip
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title={`${moveMode === 'move' ? 'Move' : 'Copy'} (A)`}>
                    <span>
                      <Button
                        onClick={handleAcceptCurrent}
                        variant={currentDecision === 'accepted' ? 'contained' : 'outlined'}
                        disabled={applyLoading}
                      >
                        {moveMode === 'move' ? 'Move' : 'Copy'}
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => {
                      if (undecidedCount > 0) {
                        setConfirmApplyOpen(true);
                      } else {
                        void handleApplyAccepted();
                      }
                    }}
                    disabled={applyLoading || acceptedCount === 0}
                  >
                    {applyLoading ? 'Applying…' : `Apply ${acceptedCount}`}
                  </Button>
                </>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
      <ContentRichPreviewDialog
        open={richPreviewOpen}
        onClose={() => setRichPreviewOpen(false)}
        html={displayHtml ?? ''}
        title={currentMatch?.subcase || 'Source row preview'}
      />
      <AdjustTextDialog
        open={stitchOpen}
        onClose={() => setStitchOpen(false)}
        currentText={
          (stitchRowId ? sourceContentsById.get(stitchRowId)?.text : undefined) ?? currentBaseText
        }
        prevText={hasPrevNeighbour ? prevNeighbourText : undefined}
        nextText={hasNextNeighbour ? nextNeighbourText : undefined}
        onSave={handleStitchSave}
      />
      <Dialog
        open={confirmApplyOpen}
        onClose={() => setConfirmApplyOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Apply accepted rows?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {undecidedCount} row{undecidedCount === 1 ? '' : 's'} weren’t reviewed and will be
            skipped. Apply {acceptedCount} accepted row{acceptedCount === 1 ? '' : 's'}
            {moveMode === 'move' ? ' (move)' : ' (copy)'}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmApplyOpen(false)}>Keep reviewing</Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              setConfirmApplyOpen(false);
              void handleApplyAccepted();
            }}
          >
            Apply {acceptedCount}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddMatchingContentDialog;
